import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    VStack,
    HStack,
    Card,
    CardBody,
    CardHeader,
    CardFooter,
    Heading,
    Text,
    useColorModeValue,
    Spinner,
    Alert,
    AlertIcon,
    AlertDescription,
    Badge,
    SimpleGrid,
    Flex,
    Icon,
    Button,
    Tooltip,
    IconButton,
    Switch,
    useDisclosure,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
} from '@chakra-ui/react';
import {
    MdFavorite,
    MdCheckCircle,
    MdError,
    MdFullscreen,
    MdFullscreenExit,
    MdBusiness,
    MdErrorOutline,
    MdAssignment,
    MdList,
    MdInfoOutline,
    MdInfo,
    MdBarChart,
    MdMoreVert,
    MdApps
} from 'react-icons/md';
import ReactApexChart from 'react-apexcharts';
import apiService from '../services/apiService';
import { formatNumber, formatDateTime, calculateDuration } from '../utils/helpers';
import { ToastManager } from '../components/Common/ToastManager';
import ClientOfflineModal from '../components/Common/ClientOfflineModal';
import ClientStatsPopup from '../components/Common/ClientStatsPopup';
import { useNavigate } from 'react-router-dom';
import AvailableApplicationsModal from '../components/Common/AvailableApplicationsModal';


/**
 * Returns true when a heartbeat client is marked internal, tolerant of the flag
 * arriving as a number (1), string ("1"), or boolean (true) from the API.
 */
const isInternalClient = (client) => {
    const v = client?.isInternal;
    return v === 1 || v === '1' || v === true || v === 'true';
};

/**
 *  Analytics Page Component with Issues management integration
 */
function Analytics({ userProfile, appConfig }) {
    // Move ALL useColorModeValue hooks to the top before any state or logic
    const cardBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const textColor = useColorModeValue('gray.600', 'gray.300');
    const headingColor = useColorModeValue('gray.800', 'white');
    const chartTheme = useColorModeValue('light', 'dark');
    const fullscreenBg = useColorModeValue('gray.50', 'gray.900');
    const prismVersionClr=useColorModeValue('blue.600', 'blue.300');

    const [heartbeatData, setHeartbeatData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    // Animation state for heartbeat charts
    const [heartbeatOffset, setHeartbeatOffset] = useState(0);

    // Fullscreen and filtering state - Default to 'issues' filter
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [viewFilter, setViewFilter] = useState('issues'); // Locked to 'issues' — analytics is an issues-only view
    const [showInternal, setShowInternal] = useState(false); // When false, internal clients are hidden
    const [filteredClients, setFilteredClients] = useState([]);
    const [clientsWithIssues, setClientsWithIssues] = useState([]);
    const [issueCountsByClient, setIssueCountsByClient] = useState({});

    // Issues statistics
    const [issuesTakenCount, setIssuesTakenCount] = useState(0);
    const [totalIssuesCount, setTotalIssuesCount] = useState(0);

    // Auto-refresh interval refs
    const heartbeatInterval = useRef(null);
    const navigate = useNavigate();

    // //Client offline history 
    // const { isOpen, onOpen, onClose } = useDisclosure();
    const {
        isOpen: isClientHistoryOpen,
        onOpen: onClientHistoryOpen,
        onClose: onClientHistoryClose
    } = useDisclosure();

    const {
        isOpen: isStatsModalOpen,
        onOpen: onStatsModalOpen,
        onClose: onStatsModalClose
    } = useDisclosure();

    const [clientHealthHistory, setClientHealthHistory] = useState([]);
    const [clientHistoryName, setClientHistoryName] = useState([]);

    //click stats
    const [selectedClient, setSelectedClient] = useState(null);
    const [clientStatsData, setClientStatsData] = useState(null);
    const [clientStatsUpdatedTime, setClientStatsUpdatedTime] = useState("");

    //mapped applications
    const [isAvailableAppsOpen, setIsAvailableAppsOpen] = useState(false);
    const [selectedClientForAvailableApps, setSelectedClientForAvailableApps] = useState(null);
    const [availableApplicationsData, setAvailableApplicationsData] = useState([]);
    const [isLoadingAvailableApps, setIsLoadingAvailableApps] = useState(false);

    /**
     * Load analytics data with Issues integration - Filter persistence maintained
     */
    const loadAnalyticsData = useCallback(async (isAutoRefresh = false) => {
        try {
            setError(null);
            if (!isAutoRefresh) {
                setLoading(true);
            }


            // Load heartbeat data, issue counts, and taken issues count
            const [heartbeatResponse, issueCountsResponse, takenCountResponse] = await Promise.all([
                apiService.getHeartbeatStatus(),
                apiService.getIssueCountByClient(),
                apiService.getTakenIssueCount()
            ]);

            if (heartbeatResponse.success) {
                const heartbeatDataResponse = heartbeatResponse.data;
                setHeartbeatData(heartbeatDataResponse);

                // Process issue counts by client
                let issueCounts = {};
                let totalIssues = 0;
                if (issueCountsResponse.success) {
                    issueCounts = issueCountsResponse.data || {};
                    // Calculate total issues from all clients
                    totalIssues = Object.values(issueCounts).reduce((sum, count) => sum + count, 0);
                }
                setIssueCountsByClient(issueCounts);
                setTotalIssuesCount(totalIssues);

                // Set taken issues count
                if (takenCountResponse.success) {
                    setIssuesTakenCount(takenCountResponse.data?.takenIssueCount || 0);
                }

                // Calculate clients with issues based on issue counts
                const issueClients = heartbeatDataResponse.clients?.filter(client => {
                    return issueCounts[client.clientId] > 0;
                }) || [];

                setClientsWithIssues(issueClients);

                // Always use current viewFilter state - no override during auto-refresh
                updateFilteredClients(viewFilter, heartbeatDataResponse.clients, issueClients, showInternal);
            }

            setLastUpdated(new Date());

        } catch (error) {
            setError(error.message || 'Failed to load analytics data');
            if (!isAutoRefresh) {
                ToastManager.showError('Failed to refresh analytics data');
            }
        } finally {
            if (!isAutoRefresh) {
                setLoading(false);
            }
        }
    }, [viewFilter, showInternal]);

    /**
     * Update filtered clients based on selected view
     * Wrapped in useCallback with proper dependencies
     */
    const updateFilteredClients = useCallback((filter, allClients, issueClients, includeInternal = false) => {
        if (!allClients) return;

        let filtered = [];
        switch (filter) {
            case 'online':
                filtered = allClients.filter(client => client.status === 'Online');
                break;
            case 'offline':
                filtered = allClients.filter(client => client.status === 'Offline');
                break;
            case 'issues':
                filtered = issueClients || [];
                break;
            default:
                filtered = allClients;
        }

        // Internal clients are hidden unless "Show Internal" is on. Accept whatever
        // shape the API sends the flag in (1, "1", true, "true").
        if (!includeInternal) {
            filtered = filtered.filter(client => !isInternalClient(client));
        }

        setFilteredClients(filtered);
    }, []);

    /**
     * Setup auto-refresh for analytics data
     * Wrapped in useCallback with proper dependencies
     */
    const setupAutoRefresh = useCallback(() => {
        // Heartbeat updates every 15 seconds
        heartbeatInterval.current = setInterval(() => {
            loadAnalyticsData(true); // Pass true to indicate this is auto-refresh
        }, 15000);
    }, [loadAnalyticsData]);

    /**
     * Cleanup intervals on unmount
     * Wrapped in useCallback to prevent recreation
     */
    const cleanup = useCallback(() => {
        if (heartbeatInterval.current) {
            clearInterval(heartbeatInterval.current);
            heartbeatInterval.current = null;
        }
    }, []);

    /**
     * Initialize analytics and start auto-refresh
     * Properly manages dependencies to prevent infinite loops
     */
    useEffect(() => {
        loadAnalyticsData();
        setupAutoRefresh();

        return cleanup;
    }, [loadAnalyticsData, setupAutoRefresh, cleanup]);

    /**
     * Animation interval for moving heartbeat
     */
    useEffect(() => {
        const animationInterval = setInterval(() => {
            setHeartbeatOffset(prev => (prev + 1) % 100);
        }, 150); // Adjust speed as needed

        return () => clearInterval(animationInterval);
    }, []);

    /**
     * Handle filter change for heartbeat view
     */
    const handleFilterChange = useCallback((filter) => {
        setViewFilter(filter);
        updateFilteredClients(filter, heartbeatData?.clients, clientsWithIssues, showInternal);
    }, [heartbeatData?.clients, clientsWithIssues, updateFilteredClients, showInternal]);

    /**
     * Toggle whether internal clients are shown in the heartbeat grid
     */
    const handleShowInternalToggle = useCallback((checked) => {
        setShowInternal(checked);
        updateFilteredClients(viewFilter, heartbeatData?.clients, clientsWithIssues, checked);
    }, [viewFilter, heartbeatData?.clients, clientsWithIssues, updateFilteredClients]);

    /**
     * Toggle fullscreen mode
     */
    const toggleFullscreen = useCallback(() => {
        setIsFullscreen(prev => !prev);
    }, []);

    /**
     * Navigate to Total Issues - Issues tab (tab 0)
     */
    const handleViewTotalIssues = useCallback(() => {
        try {
            //const basePath = window.ADI_CONFIG?.app?.subDirectory || '';
            const issuesPath = `/issues?tab=0`;
            // window.location.href = issuesPath;
            navigate(issuesPath)
        } catch (error) {
            console.error('Failed to navigate to total issues:', error);
            ToastManager.showError('Failed to open total issues');
        }
    }, [navigate]);

    /**
     * Navigate to Issues Taken - Issues Taken tab (tab 1)
     */
    const handleViewIssuesTaken = useCallback(() => {
        try {
            const issuesPath = `/issues?tab=1`;
            // window.location.href = issuesPath;
            navigate(issuesPath)
        } catch (error) {
            console.error('Failed to navigate to issues assigned:', error);
            ToastManager.showError('Failed to open issues assigned');
        }
    }, [navigate]);
    /**
     * Handle client issues navigation - redirect to Issues page with client filter
     */
    const handleViewClientIssues = useCallback((clientId, clientName, tabIndex = 0) => {
        try {
            const params = new URLSearchParams();
            params.set('clientId', clientId.toString());
            params.set('clientName', clientName);
            params.set('tab', tabIndex.toString());

            const issuesPath = `/issues?${params.toString()}`;
            navigate(issuesPath);
        } catch (error) {
            console.error('Failed to navigate to client issues:', error);
            ToastManager.showError('Failed to open client issues');
        }
    }, [navigate]);

    /**
     * Handle heartbeat chart click - redirect to Issues page with client filter
     */
    const handleHeartbeatChartClick = useCallback((clientId, clientName) => {
        try {
            const params = new URLSearchParams();
            params.set('clientId', clientId.toString());
            params.set('clientName', clientName);
            const clientProfilePath= `/clientprofile?${params.toString()}`
            //const issuesPath = `/issues?${params.toString()}`;
            navigate(clientProfilePath);
        } catch (error) {
            console.error('Failed to navigate to Client Profile:', error);
            ToastManager.showError('Failed to open Client Profile');
        }
    }, [navigate]);
    /**
     * Generate compact EKG heartbeat chart for responsive design with moving animation
     */
    const generateCompactHeartbeatChart = useCallback((clientName, isOnline, theme) => {
        const chartHeight = 80;

        if (!isOnline) {
            // Straight line for offline clients
            const flatLineData = Array.from({ length: 50 }, (_, i) => ({
                x: i,
                y: 50
            }));

            const chartOptions = {
                chart: {
                    type: 'line',
                    height: chartHeight,
                    toolbar: { show: false },
                    background: 'transparent',
                    animations: { enabled: false },
                    sparkline: { enabled: true }
                },
                stroke: {
                    curve: 'straight',
                    width: 2,
                    colors: ['#ef4444']
                },
                xaxis: { show: false },
                yaxis: { show: false, min: 0, max: 100 },
                grid: { show: false },
                tooltip: { enabled: false },
                theme: { mode: theme }
            };

            return (
                <ReactApexChart
                    options={chartOptions}
                    series={[{ name: 'Heartbeat', data: flatLineData }]}
                    type="line"
                    height={chartHeight}
                />
            );
        }

        // Classic EKG heartbeat pattern with closer heartbeats and shorter baselines
        const basePattern = [
            // Short baseline
            50, 50, 50,
            // P wave (sharp small bump)
            50, 55, 50,
            // Short flat before QRS
            50,
            // QRS complex (precise sharp spike)
            50, 90, 50, 15, 50, 70, 50,
            // Short baseline
            50, 50, 50,
            // Next heartbeat - P wave (sharp)
            50, 55, 50,
            // Short flat before QRS
            50,
            // QRS complex (precise sharp spike)
            50, 90, 50, 15, 50, 70, 50,
            // Short baseline
            50, 50, 50,
            // Another heartbeat
            50, 55, 50,
            50,
            50, 90, 50, 15, 50, 70, 50,
            // Short baseline
            50, 50
        ];

        // Create moving animation from right to left
        const animatedData = Array.from({ length: 50 }, (_, i) => {
            const patternIndex = (i + heartbeatOffset) % basePattern.length;
            return {
                x: i,
                y: basePattern[patternIndex]
            };
        });

        const chartOptions = {
            chart: {
                type: 'line',
                height: chartHeight,
                toolbar: { show: false },
                background: 'transparent',
                animations: { enabled: false },
                sparkline: { enabled: true }
            },
            stroke: {
                curve: 'straight', // Changed to straight for sharp peaks and lows
                width: 2,
                colors: ['#10b981']
            },
            xaxis: { show: false },
            yaxis: { show: false, min: 0, max: 100 },
            grid: { show: false },
            tooltip: { enabled: false },
            theme: { mode: theme }
        };

        return (
            <ReactApexChart
                options={chartOptions}
                series={[{ name: 'Heartbeat', data: animatedData }]}
                type="line"
                height={chartHeight}
            />
        );
    }, [heartbeatOffset]);

    /**
     * Stat Card Component with click handlers
     */
    const StatCard = useCallback(({ title, value, icon, color = 'blue', status, onClick, isActive = false }) => (
        <Card
            bg={cardBg}
            borderColor={isActive ? 'brand.500' : borderColor}
            borderWidth={isActive ? 2 : 1}
            cursor={onClick ? 'pointer' : 'default'}
            onClick={onClick}
            _hover={onClick ? {
                borderColor: 'brand.300',
                transform: 'translateY(-2px)',
                boxShadow: 'lg'
            } : {}}
            transition="all 0.2s"
        >
            <CardBody>
                <Flex justify="space-between" align="start">
                    <Box>
                        <Text fontSize="sm" color={textColor} mb={2}>
                            {title}
                        </Text>
                        <Text fontSize="2xl" fontWeight="bold" color={headingColor}>
                            {formatNumber(value)}
                        </Text>
                        {status && (
                            <Badge colorScheme={color} variant="subtle" mt={2}>
                                {status}
                            </Badge>
                        )}
                    </Box>
                    <Icon as={icon} w={6} h={6} color={`${color}.500`} />
                </Flex>
            </CardBody>
        </Card>
    ), [cardBg, borderColor, textColor, headingColor]);

    /**
     * Calculate responsive grid columns based on client count
     */
    const getGridColumns = useCallback((clientCount) => {
        if (clientCount <= 2) return { base: 1, md: 2 };
        if (clientCount <= 4) return { base: 1, md: 2, lg: 4 };
        if (clientCount <= 6) return { base: 1, md: 2, lg: 3 };
        if (clientCount <= 9) return { base: 1, md: 2, lg: 3, xl: 3 };
        if (clientCount <= 12) return { base: 1, md: 2, lg: 3, xl: 4 };
        return { base: 1, md: 2, lg: 3, xl: 5 };
    }, []);

    /**
     * Render loading state
     */
    if (loading) {
        return (
            <Flex justify="center" align="center" minH="400px">
                <VStack spacing={4}>
                    <Spinner size="xl" color="brand.500" thickness="4px" />
                    <Text color={textColor}>Loading analytics...</Text>
                </VStack>
            </Flex>
        );
    }

    /**
     * Render error state
     */
    if (error) {
        return (
            <Alert status="error" borderRadius="md">
                <AlertIcon />
                <AlertDescription>
                    {error}. Analytics will retry automatically.
                </AlertDescription>
            </Alert>
        );
    }

    const totalClients = heartbeatData?.clients?.length || 0;
    const onlineClients = heartbeatData?.summary?.online || 0;
    const offlineClients = heartbeatData?.summary?.offline || 0;
    const clientsWithIssuesCount = clientsWithIssues.length;

    const handleClientOfflineHistoryIconClick = async (clientId, clientName) => {
        // onOpen();
        onClientHistoryOpen();
        var response = await apiService.fetchClientOfflineHistory(clientId);
        setClientHealthHistory(response.data.clientHistory);
        setClientHistoryName(clientName);
    }

    const handleClickStatsIconClick = async (clientId, clientName) => {
        var response = await apiService.fetchClientClickHistory(clientId);
        var statsData = JSON.parse(response.data.clickHistory.click_data);

        // Set the selected client and stats data
        setSelectedClient({ id: clientId, name: clientName });
        setClientStatsData(statsData);
        setClientStatsUpdatedTime(response.data.clickHistory.last_updated_time)
        // Open the modal using useDisclosure
        onStatsModalOpen();
    }

    const handleStatsModalClose = () => {
        onStatsModalClose();
        setSelectedClient(null);
        setClientStatsData(null);
        setClientStatsUpdatedTime("");
    };




    const handleAvailableApplicationsClick = async (clientId, clientName) => {
        setSelectedClientForAvailableApps({ id: clientId, name: clientName });
        setIsAvailableAppsOpen(true);
        setIsLoadingAvailableApps(true);

        try {
            const apiParams = {};
            if (clientId) {
                apiParams.clientId = clientId;
            }
            // Replace with your actual API endpoint for available applications
            const response = await apiService.fetchClientApplicationMappings(apiParams);

            if (response.success && response.data) {
                setAvailableApplicationsData(Array.isArray(response.data) ? response.data : []);
            }
        } catch (error) {
            console.error('Error fetching available applications:', error);
            setAvailableApplicationsData([]);
        } finally {
            setIsLoadingAvailableApps(false);
        }
    };

    // Close handler to clean up state
    const onAvailableAppsClose = () => {
        setIsAvailableAppsOpen(false);
        setSelectedClientForAvailableApps(null);
        setAvailableApplicationsData([]);
        setIsLoadingAvailableApps(false);
    };

    return (
        <Box h={isFullscreen ? "100vh" : "auto"} position={isFullscreen ? "fixed" : "relative"}
            top={isFullscreen ? 0 : "auto"} left={isFullscreen ? 0 : "auto"}
            right={isFullscreen ? 0 : "auto"} bottom={isFullscreen ? 0 : "auto"}
            zIndex={isFullscreen ? 9999 : "auto"} bg={isFullscreen ? fullscreenBg : 'transparent'}
            p={isFullscreen ? 6 : 0} overflowY={isFullscreen ? "auto" : "visible"}>

            <VStack spacing={6} align="stretch" h="full">
                {/* Page Header with Fullscreen Toggle */}
                <Flex justify="space-between" align="center">
                    <Box>
                        <Heading size="lg" color={headingColor} mb={2}>
                            Prism Monitor Analytics
                        </Heading>
                        <HStack spacing={4} color={textColor} fontSize="sm">
                            <Text>Real-time client monitoring {viewFilter !== 'all' && `(${viewFilter} view)`}</Text>
                            {lastUpdated && (
                                <Badge colorScheme="green" variant="subtle">
                                    Last updated: {formatDateTime(lastUpdated, { showDate: false })}
                                </Badge>
                            )}
                        </HStack>
                    </Box>

                    <HStack spacing={3}>
                        {/* Current view label + Show Internal toggle */}
                        <HStack spacing={3}>
                            <Badge
                                colorScheme={viewFilter === 'online' ? 'green' : viewFilter === 'offline' ? 'red' : viewFilter === 'all' ? 'brand' : 'orange'}
                                variant="solid"
                            >
                                {viewFilter === 'online' ? 'Online Only' : viewFilter === 'offline' ? 'Offline Only' : viewFilter === 'all' ? 'All Clients' : 'Issues Only'}
                            </Badge>
                            <HStack spacing={2}>
                                <Switch
                                    id="show-internal-toggle"
                                    colorScheme="brand"
                                    isChecked={showInternal}
                                    onChange={(e) => handleShowInternalToggle(e.target.checked)}
                                />
                                <Text fontSize="sm" color={textColor}>Show Internal</Text>
                            </HStack>
                        </HStack>

                        <Tooltip label={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"} hasArrow>
                            <IconButton
                                icon={isFullscreen ? <MdFullscreenExit /> : <MdFullscreen />}
                                onClick={toggleFullscreen}
                                variant="outline"
                                size="sm"
                                aria-label="Toggle fullscreen"
                            />
                        </Tooltip>
                    </HStack>
                </Flex>

                {/* Statistics Grid with Total Issues */}
                <SimpleGrid columns={{ base: 1, md: 2, lg: 6 }} spacing={6}>
                    <StatCard
                        title="Total Clients"
                        value={totalClients}
                        icon={MdBusiness}
                        color="purple"
                        status="Active"
                        onClick={() => handleFilterChange('all')}
                        isActive={viewFilter === 'all'}
                    />
                    <StatCard
                        title="Online Clients"
                        value={onlineClients}
                        icon={MdCheckCircle}
                        color="green"
                        status="Connected"
                        onClick={() => handleFilterChange('online')}
                        isActive={viewFilter === 'online'}
                    />
                    <StatCard
                        title="Offline Clients"
                        value={offlineClients}
                        icon={MdError}
                        color="red"
                        status="Disconnected"
                        onClick={() => handleFilterChange('offline')}
                        isActive={viewFilter === 'offline'}
                    />
                    <StatCard
                        title="Clients with Issues"
                        value={clientsWithIssuesCount}
                        icon={MdErrorOutline}
                        color="orange"
                        status="Needs Attention"
                        onClick={() => handleFilterChange('issues')}
                        isActive={viewFilter === 'issues'}
                    />
                    <StatCard
                        title="Total Issues"
                        value={totalIssuesCount}
                        icon={MdList}
                        color="cyan"
                        status="Open"
                        onClick={handleViewTotalIssues}
                    />
                    <StatCard
                        title="Issues Assigned"
                        value={issuesTakenCount}
                        icon={MdAssignment}
                        color="blue"
                        status="In Progress"
                        onClick={handleViewIssuesTaken}
                    />
                </SimpleGrid>

                {/* Enhanced Responsive Heartbeat Grid */}
                <Box>
                    <Heading size="md" color={headingColor} mb={4}>
                        Client Heartbeat {viewFilter !== 'all' && `- ${viewFilter.charAt(0).toUpperCase() + viewFilter.slice(1)} View`}
                    </Heading>

                    {filteredClients && filteredClients.length > 0 ? (
                        <SimpleGrid
                            columns={getGridColumns(filteredClients.length)}
                            spacing={4}
                        >
                            {filteredClients
                                .sort((a, b) => {
                                    const issueCountA = issueCountsByClient[a.clientId] || 0;
                                    const issueCountB = issueCountsByClient[b.clientId] || 0;
                                    return issueCountB - issueCountA;
                                })
                                .map((client) => {
                                    const issueCount = issueCountsByClient[client.clientId] || 0;
                                    return (
                                        <Card key={client.clientId} bg={cardBg} borderColor={borderColor} size="sm">
                                            <CardHeader pb={1}>
                                                <Flex justify="space-between" align="start">
                                                    <VStack align="start" spacing={1} flex="1">
                                                            <Text fontWeight="bold" fontSize="sm" noOfLines={1}>
                                                                {client.clientName}
                                                                {client.prismVersion && (
                                                                    <Text as="span" fontSize="sm" color={prismVersionClr} fontWeight="normal" ml={1}>
                                                                        ({client.prismVersion})
                                                                    </Text>
                                                                )}
                                                            </Text>
                                                        <HStack spacing={2}>
                                                            <Badge
                                                                colorScheme={client.status === 'Online' ? 'green' : 'red'}
                                                                variant="subtle"
                                                                fontSize="xs"
                                                            >
                                                                {client.status}
                                                            </Badge>
                                                        </HStack>
                                                    </VStack>
                                                    <HStack spacing={2}>
                                                        <Icon
                                                            as={MdFavorite}
                                                            color={client.status === 'Online' ? 'green.500' : 'red.500'}
                                                            w={4}
                                                            h={4}
                                                        />
                                                        {/* Three dots menu for options */}
                                                        <Menu>
                                                            <MenuButton
                                                                as={IconButton}
                                                                icon={<Icon as={MdMoreVert} />}
                                                                variant="ghost"
                                                                size="sm"
                                                                color="gray.500"
                                                                _hover={{ color: "gray.700", bg: "gray.100" }}
                                                                _active={{ bg: "gray.200" }}
                                                                aria-label="More options"
                                                            />
                                                            <MenuList fontSize="sm" minW="200px">
                                                                {/*<MenuItem
                                                                    icon={<Icon as={MdBarChart} color="blue.500" />}
                                                                    onClick={() => handleClickStatsIconClick(client.clientId, client.clientName)}
                                                                >
                                                                    View Click Statistics
                                                                </MenuItem>*/}
                                                                <MenuItem
                                                                    icon={<Icon as={MdInfoOutline} color="gray.500" />}
                                                                    onClick={() => handleClientOfflineHistoryIconClick(client.clientId, client.clientName)}
                                                                >
                                                                    Client Offline History
                                                                </MenuItem>
                                                                <MenuItem
                                                                    icon={<Icon as={MdApps} color="purple.500" />}
                                                                    onClick={() => handleAvailableApplicationsClick(client.clientId, client.clientName)}
                                                                >
                                                                    Mapped Applications
                                                                </MenuItem>
                                                            </MenuList>
                                                        </Menu>
                                                    </HStack>
                                                </Flex>
                                            </CardHeader>

                                            <CardBody py={2}
                                                onClick={() => handleHeartbeatChartClick(client.clientId, client.clientName)}
                                            >
                                                {/* Make chart clickable to navigate to issues */}
                                                <Box
                                                    cursor="pointer"
                                                    // onClick={() => handleHeartbeatChartClick(client.clientId, client.clientName)}
                                                    _hover={{ opacity: 0.8 }}
                                                    transition="opacity 0.2s"
                                                >
                                                    {generateCompactHeartbeatChart(client.clientName, client.status === 'Online', chartTheme)}
                                                </Box>
                                                <Text fontSize="xs" color={textColor} mt={1} textAlign="center" noOfLines={1}>
                                                    Last: {formatDateTime(client.lastHeartbeat, { showDate: true })}
                                                </Text>
                                            </CardBody>

                                            <CardFooter pt={1}>
                                                <HStack spacing={2} w="full" justify="center">
                                                    {issueCount > 0 ? (
                                                        <Tooltip label={`View ${issueCount} open issues`} hasArrow>
                                                            <Button
                                                                size="xs"
                                                                variant="ghost"
                                                                colorScheme="orange"
                                                                leftIcon={<MdErrorOutline />}
                                                                onClick={() => handleViewClientIssues(client.clientId, client.clientName, 0)}
                                                                flex="1"
                                                                fontSize="xs"
                                                            >
                                                                Issues ({issueCount})
                                                            </Button>
                                                        </Tooltip>
                                                    ) : (
                                                        <Button
                                                            size="xs"
                                                            variant="ghost"
                                                            colorScheme="green"
                                                            leftIcon={<MdCheckCircle />}
                                                            isDisabled
                                                            flex="1"
                                                            fontSize="xs"
                                                        >
                                                            No Issues
                                                        </Button>
                                                    )}
                                                </HStack>
                                            </CardFooter>
                                        </Card>
                                    );
                                })}
                        </SimpleGrid>
                    ) : (
                        <Card bg={cardBg} borderColor={borderColor}>
                            <CardBody>
                                <Flex justify="center" align="center" minH="200px">
                                    <VStack spacing={4}>
                                        <Icon as={MdError} w={12} h={12} color="gray.400" />
                                        <Text color={textColor} textAlign="center">
                                            No clients with issues found
                                        </Text>
                                        {!showInternal && (
                                            <Button size="sm" onClick={() => handleShowInternalToggle(true)}>
                                                Show Internal Clients
                                            </Button>
                                        )}
                                    </VStack>
                                </Flex>
                            </CardBody>
                        </Card>
                    )}
                    <ClientOfflineModal
                        isOpen={isClientHistoryOpen}
                        onClose={onClientHistoryClose}
                        clientHistoryName={clientHistoryName}
                        clientHealthHistory={clientHealthHistory}
                        formatDateTime={formatDateTime}
                        calculateDuration={calculateDuration}
                    />
                    {selectedClient && (
                        <ClientStatsPopup
                            isOpen={isStatsModalOpen}
                            onClose={handleStatsModalClose}
                            clientName={selectedClient.name}
                            clientId={selectedClient.id}
                            statsData={clientStatsData}
                            updatedTime={clientStatsUpdatedTime}
                        />)}
                    {selectedClientForAvailableApps && (
                        <AvailableApplicationsModal
                            isOpen={isAvailableAppsOpen}
                            onClose={onAvailableAppsClose}
                            availableApplications={availableApplicationsData}
                            isLoading={isLoadingAvailableApps}
                            clientName={selectedClientForAvailableApps.name}
                            clientId={selectedClientForAvailableApps.id}
                        />
                    )}
                </Box>
            </VStack>
        </Box>
    );
}

export default Analytics;